import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Sparkles, Check, ArrowRight, Zap, TrendingUp, Bot, Flame,
  CalendarRange, Drama, MicVocal, Dna, Gauge, Radar, MessageCircleHeart,
  Lock, Shield, Crown, Rocket,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const KFN = 'Kari Future Now Creations™';

const SUPER_FEATURES = [
  { icon: Flame, title: 'Viral Pack', desc: 'ONE topic → 5 viral variants + engagement scores + best hashtags + best post time. Picks the A/B winner for you.', accent: 'from-orange-500 to-red-600' },
  { icon: CalendarRange, title: 'Burst Calendar', desc: '30 days of original content in 60 seconds. A whole month of posts before your coffee gets cold.', accent: 'from-amber-500 to-orange-600' },
  { icon: Drama, title: 'Persona Rewriter', desc: 'Same idea, 4 distinct brand voices: Hype Mogul, Calm Authority, Bestie Vibe, Luxury Whisper. Pick the one that hits.', accent: 'from-pink-500 to-red-500' },
  { icon: MicVocal, title: 'Scroll-Stopper', desc: '10 first-line hooks ranked by scroll-stop strength. Steal the 0.4-second window before the thumb keeps swiping.', accent: 'from-yellow-500 to-orange-500' },
  { icon: Dna, title: 'Brand DNA Engine', desc: 'Paste 3 of YOUR past posts. AI extracts your unique voice DNA and uses it forever. Other tools auto-match your tone.', accent: 'from-emerald-500 to-teal-500' },
  { icon: Gauge, title: 'Engagement Oracle', desc: 'Score any draft 0-100 BEFORE you post. Predicts likes, shares, save rate + 3 ruthless upgrades to fix it.', accent: 'from-fuchsia-500 to-purple-600' },
  { icon: Radar, title: 'Trend Radar', desc: '5 hottest content angles in your niche right now — with viral hooks ready to copy-paste.', accent: 'from-cyan-500 to-blue-500' },
  { icon: MessageCircleHeart, title: 'Comment Killer', desc: 'Paste any comment → 3 witty on-brand replies in YOUR voice. Engage, defuse, or convert. Save hours per week.', accent: 'from-rose-500 to-pink-600' },
  { icon: Zap, title: 'Smart Share & Destinations', desc: 'One-tap publish to FB, IG, X, Threads, LinkedIn, WhatsApp — caption pre-filled. Plus Zapier/Buffer/Publer auto-send. ZERO Facebook App needed.', accent: 'from-orange-500 to-amber-500' },
];

const INCLUDED = [
  'All 9 AI Lab superpowers (unlimited)',
  'Voice, text, image inputs',
  'Brand DNA voice cloning',
  'Engagement prediction before you post',
  'One-tap post to FB / IG / X / Threads / LinkedIn (zero FB App needed)',
  'Auto-send via Zapier, Buffer, Publer, Make, or n8n webhook',
  'Every output stamped "Made with Kari Future Now Creations™"',
  'GPT-5.1 + Whisper + Vision (the 3 strongest AI models)',
  '30-day money-back guarantee',
];

const AIAutoPoster = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = useCallback(async () => {
    if (!isAuthenticated) {
      toast.info('Quick step: create an account or log in to subscribe', { duration: 2500 });
      navigate('/login?redirect=/ai-auto-poster&action=subscribe');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/subscriptions/create`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.success("You're already subscribed!", { description: 'Taking you to the AI Lab...' });
        navigate('/ai-lab');
      }
    } catch {
      toast.error('Failed to start subscription. Please try again.');
      setLoading(false);
    }
  }, [isAuthenticated, navigate]);

  // Auto-trigger subscribe after login bounce-back
  useEffect(() => {
    if (!authLoading && isAuthenticated && searchParams.get('action') === 'subscribe') {
      handleSubscribe();
    }
  }, [authLoading, isAuthenticated, searchParams, handleSubscribe]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a0f] via-[#11111a] to-[#0a0a0f] text-white pt-24 pb-20 relative overflow-hidden" data-testid="autoposter-page">
      {/* Ambient blobs */}
      <div className="pointer-events-none absolute top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-orange-500/20 blur-[140px]" />
      <div className="pointer-events-none absolute top-1/3 -right-32 w-[32rem] h-[32rem] rounded-full bg-red-600/20 blur-[160px]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Hero */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-300 text-xs font-semibold mb-5">
            <Crown className="w-3.5 h-3.5" /> {KFN} — AI AUTO-POSTER
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black leading-[1.0] tracking-tight max-w-5xl mx-auto">
            <span className="bg-gradient-to-r from-orange-400 via-amber-300 to-red-500 bg-clip-text text-transparent">
              The Only AI Tool
            </span>
            <br />
            <span className="text-white">With 9 Superpowers</span>
            <br />
            <span className="text-white/40 text-2xl md:text-3xl font-bold">no other app has.</span>
          </h1>
          <p className="text-base md:text-lg text-white/70 mt-5 max-w-3xl mx-auto leading-relaxed">
            Generate viral content. Predict engagement <em>before</em> you post. Clone your own brand voice.
            One-tap publish to Facebook, Instagram, X, Threads &amp; LinkedIn <strong className="text-orange-300">— with zero Facebook App needed.</strong>{' '}
            Powered by GPT-5.1 + Whisper + Vision, signed by <span className="text-orange-300 font-semibold">{KFN}</span>.
          </p>

          {/* Pricing Card */}
          <Card className="max-w-md mx-auto mt-10 bg-gradient-to-br from-orange-500/15 via-red-600/10 to-amber-500/10 border-2 border-orange-500/40 backdrop-blur-md shadow-[0_0_60px_rgba(249,115,22,0.25)]">
            <CardHeader className="text-center pb-2">
              <div className="flex justify-center mb-3">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-xl shadow-orange-500/40">
                  <Rocket className="w-8 h-8 text-white" />
                </div>
              </div>
              <CardTitle className="text-5xl font-black text-white">
                $29.99<span className="text-lg font-medium text-white/60">/mo</span>
              </CardTitle>
              <CardDescription className="text-white/70">
                Cancel anytime · 30-Day money-back guarantee
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={handleSubscribe}
                disabled={loading}
                size="lg"
                data-testid="autoposter-subscribe-btn"
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold text-base py-6 shadow-xl shadow-orange-500/40 hover:shadow-orange-500/60 transition-all"
              >
                {loading ? 'Loading...' : (<>Start AI Lab — $29.99/mo<ArrowRight className="ml-2 w-5 h-5" /></>)}
              </Button>
              <div className="flex items-center justify-center gap-4 mt-4 text-xs text-white/60">
                <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Stripe secured</span>
                <span className="inline-flex items-center gap-1"><Shield className="w-3 h-3" /> 30-day refund</span>
                <span className="inline-flex items-center gap-1"><Zap className="w-3 h-3" /> Instant access</span>
              </div>
              <p className="text-xs text-center text-orange-300 mt-3">
                <TrendingUp className="w-3 h-3 inline mr-1" />
                Join 2,500+ creators using {KFN}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* The 8 Superpowers Grid */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <Badge className="mb-3 bg-orange-500/10 border border-orange-500/30 text-orange-300 hover:bg-orange-500/15">The 9 Superpowers</Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight">
              <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">No Other AI Tool</span>
              <span className="text-white"> Does This.</span>
            </h2>
            <p className="text-white/60 mt-3 max-w-2xl mx-auto">
              Every feature below is exclusive to {KFN}. You won't find this stack anywhere else for $29.99/mo.
              <br />
              <span className="text-orange-300">⚡ And NONE of them require a Facebook App or secret key.</span>
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {SUPER_FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <Card key={f.title} className="bg-white/5 border border-white/10 backdrop-blur-md hover:border-orange-500/50 transition-all group relative overflow-hidden">
                  <div className="absolute -top-12 -right-12 w-24 h-24 rounded-full bg-orange-500/0 group-hover:bg-orange-500/15 blur-2xl transition-all" />
                  <CardContent className="p-5 relative">
                    <Badge className="absolute top-4 right-4 bg-black/40 text-white/70 border border-white/15 text-[10px]">#{String(i + 1).padStart(2, '0')}</Badge>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.accent} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-white mt-4">{f.title}</h3>
                    <p className="text-sm text-white/65 mt-1.5 leading-relaxed">{f.desc}</p>
                    <div className="text-[10px] text-orange-300/70 mt-3 inline-flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Powered by {KFN}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* What's Included */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          <Card className="bg-white/5 border border-white/10 backdrop-blur-md">
            <CardHeader>
              <CardTitle className="text-2xl text-white flex items-center gap-2"><Bot className="w-6 h-6 text-orange-400" /> What's Included</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {INCLUDED.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-white/85">
                    <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500/10 to-red-600/10 border border-orange-500/30 backdrop-blur-md">
            <CardHeader>
              <CardTitle className="text-2xl text-white flex items-center gap-2"><Crown className="w-6 h-6 text-orange-400" /> Compare</CardTitle>
              <CardDescription className="text-white/70">Why {KFN} crushes the competition</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                {[
                  ['9 unique AI superpowers', '✅', '❌'],
                  ['Brand DNA voice cloning', '✅', '❌'],
                  ['Pre-publish engagement score', '✅', '❌'],
                  ['30-day burst calendar', '✅', '❌'],
                  ['Comment-killer auto replies', '✅', '❌'],
                  ['One-tap post to 7 platforms', '✅', '❌'],
                  ['Works WITHOUT FB App / secret', '✅', '❌'],
                  ['GPT-5.1 + Whisper + Vision', '✅', '❌'],
                  ['Price under $30/mo', '✅ $29.99', '$79+'],
                ].map(([feature, us, them]) => (
                  <div key={feature} className="grid grid-cols-3 items-center text-white/85 py-1.5 border-b border-white/5 last:border-0">
                    <div>{feature}</div>
                    <div className="text-center text-emerald-400 font-bold">{us}</div>
                    <div className="text-center text-white/40">{them}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Final CTA */}
        <Card className="relative overflow-hidden border-0 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500 via-red-600 to-amber-500" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,white,transparent_50%)] opacity-15" />
          <CardContent className="relative z-10 p-10 md:p-14 text-center text-white">
            <Crown className="w-12 h-12 mx-auto mb-4 text-white" />
            <h2 className="text-3xl md:text-5xl font-black mb-4">Stop Posting Like Everyone Else.</h2>
            <p className="text-base md:text-lg mb-8 text-white/90 max-w-2xl mx-auto">
              Plug into 9 AI superpowers no other tool has. Built &amp; signed by <strong>{KFN}</strong>.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                onClick={handleSubscribe}
                disabled={loading}
                size="lg"
                variant="secondary"
                data-testid="final-cta-subscribe"
                className="bg-white text-orange-600 hover:bg-white/90 font-bold text-base px-8 py-6 shadow-lg"
              >
                {loading ? 'Loading...' : (<>Start AI Lab — $29.99/mo <ArrowRight className="ml-2 w-5 h-5" /></>)}
              </Button>
              <Link to="/ai-lab">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-bold">
                  <Bot className="mr-2 w-5 h-5" /> Peek the Lab
                </Button>
              </Link>
            </div>
            <p className="text-xs mt-6 text-white/80">
              Made with <span className="font-bold underline">{KFN}</span> · GPT-5.1 · Whisper · Vision
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AIAutoPoster;
